﻿import { useEffect, useState } from 'react';
import {
  Page, Card, Button, Banner, Badge, Text, Select,
  DataTable, EmptyState, Spinner, Layout, BlockStack,
  InlineStack, InlineGrid, Box, Pagination,
} from '@shopify/polaris';
import { CashDollarIcon, CartUpIcon, ChartLineIcon, TeamIcon } from '@shopify/polaris-icons';
import { useSalesInsights } from '../../hooks/useSalesInsights';
import type { SalesInsightSnapshot } from '../../api/salesInsights';
import { StatCard } from '../shared/StatCard';

function impactBadge(rate: number) {
  const tone = rate >= 30 ? 'success' : rate >= 15 ? 'attention' : 'info';
  return <Badge tone={tone}>{`${rate}% repeat`}</Badge>;
}

interface DetailProps {
  snapshot: SalesInsightSnapshot;
  onBack: () => void;
  onDelete: (id: number) => void;
}

function SnapshotDetail({ snapshot, onBack, onDelete }: DetailProps) {
  return (
    <Page fullWidth
      title={`Sales Snapshot: ${snapshot.period_start} – ${snapshot.period_end}`}
      backAction={{ content: 'All Snapshots', onAction: onBack }}
      secondaryActions={[{
        content: 'Delete',
        destructive: true,
        onAction: () => { onDelete(snapshot.id); onBack(); },
      }]}
    >
      <Layout>
        <Layout.Section>
          <BlockStack gap="400">
            <InlineGrid columns={{ xs: 1, sm: 4 }} gap="300">
              <StatCard label="Revenue" value={`$${snapshot.total_revenue.toLocaleString()}`} icon={CashDollarIcon} iconColor="green" />
              <StatCard label="Orders" value={String(snapshot.order_count)} icon={CartUpIcon} iconColor="indigo" />
              <StatCard label="Avg Order Value" value={`$${snapshot.average_order_value.toFixed(2)}`} icon={ChartLineIcon} iconColor="purple" />
              <StatCard label="Unique Customers" value={String(snapshot.unique_customer_count)} icon={TeamIcon} iconColor="blue" deltaLabel={`${snapshot.repeat_customer_rate}% repeat`} />
            </InlineGrid>

            {snapshot.ai_summary && (
              <Card>
                <BlockStack gap="200">
                  <Text as="h2" variant="headingMd">Summary</Text>
                  <Text as="p" variant="bodyMd">{snapshot.ai_summary}</Text>
                </BlockStack>
              </Card>
            )}

            <Card>
              <BlockStack gap="400">
                <Text as="h2" variant="headingMd">Top Products by Revenue</Text>
                {snapshot.top_products.length === 0 ? (
                  <Text as="p" tone="subdued">No product data for this period.</Text>
                ) : (
                  <DataTable
                    columnContentTypes={['text', 'numeric', 'numeric']}
                    headings={['Product', 'Revenue', 'Units Sold']}
                    rows={snapshot.top_products.map((p) => [p.title, `$${p.revenue.toLocaleString()}`, String(p.units)])}
                  />
                )}
              </BlockStack>
            </Card>

            <Card>
              <BlockStack gap="400">
                <Text as="h2" variant="headingMd">Top Customers by Spend</Text>
                {snapshot.top_customers.length === 0 ? (
                  <Text as="p" tone="subdued">No customer data for this period.</Text>
                ) : (
                  <DataTable
                    columnContentTypes={['text', 'text', 'numeric', 'numeric']}
                    headings={['Customer', 'Email', 'Total Spent', 'Orders']}
                    rows={snapshot.top_customers.map((c) => [c.name, c.email ?? '—', `$${c.total_spent.toLocaleString()}`, String(c.order_count)])}
                  />
                )}
              </BlockStack>
            </Card>

            <Card>
              <BlockStack gap="400">
                <Text as="h2" variant="headingMd">AI Recommendations</Text>
                <DataTable
                  columnContentTypes={['text', 'text', 'text']}
                  headings={['Title', 'Action', 'Expected Impact']}
                  rows={snapshot.ai_recommendations.map((r) => [r.title, r.action, r.expected_impact])}
                />
              </BlockStack>
            </Card>
          </BlockStack>
        </Layout.Section>
      </Layout>
    </Page>
  );
}

const PERIOD_OPTIONS = [
  { label: 'Last 30 days', value: '30' },
  { label: 'Last 60 days', value: '60' },
  { label: 'Last 90 days', value: '90' },
];

export function SalesInsightPanel() {
  const {
    snapshots, activeSnapshot, loading, generating, error,
    total, page, lastPage, fetchSnapshots, generate, deleteSnapshot, setActiveSnapshot, setError,
  } = useSalesInsights();

  const [days, setDays] = useState('60');

  useEffect(() => {
    fetchSnapshots(1);
  }, []);

  if (activeSnapshot) {
    return (
      <SnapshotDetail
        snapshot={activeSnapshot}
        onBack={() => setActiveSnapshot(null)}
        onDelete={deleteSnapshot}
      />
    );
  }

  const latest = snapshots[0];

  return (
    <Page fullWidth
      title="Sales & Customer Insights"
      subtitle="AI-powered revenue and customer analysis from your recent orders"
    >
      <Layout>
        <Layout.Section>
          <BlockStack gap="400">
          {error && (
            <Banner tone="critical" onDismiss={() => setError(null)}>
              <p>{error}</p>
            </Banner>
          )}

          {latest && (
            <InlineGrid columns={{ xs: 1, sm: 4 }} gap="300">
              <StatCard label="Revenue (latest snapshot)" value={`$${latest.total_revenue.toLocaleString()}`} icon={CashDollarIcon} iconColor="green" />
              <StatCard label="Orders" value={String(latest.order_count)} icon={CartUpIcon} iconColor="indigo" />
              <StatCard label="Avg Order Value" value={`$${latest.average_order_value.toFixed(2)}`} icon={ChartLineIcon} iconColor="purple" />
              <StatCard label="Repeat Customer Rate" value={`${latest.repeat_customer_rate}%`} icon={TeamIcon} iconColor="blue" />
            </InlineGrid>
          )}

          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">Generate New Snapshot</Text>
              <InlineStack gap="300" blockAlign="end">
                <Box minWidth="200px">
                  <Select label="Period" options={PERIOD_OPTIONS} value={days} onChange={setDays} />
                </Box>
                <Button
                  variant="primary"
                  loading={generating}
                  onClick={() => generate({ days: Number(days) })}
                >
                  Generate Snapshot
                </Button>
              </InlineStack>
            </BlockStack>
          </Card>

          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">Snapshots ({total})</Text>

              {loading ? (
                <InlineStack align="center"><Spinner /></InlineStack>
              ) : snapshots.length === 0 ? (
                <EmptyState
                  heading="No sales insight snapshots yet"
                  image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
                  action={{ content: 'Generate First Snapshot', onAction: () => generate({ days: Number(days) }) }}
                >
                  <p>Generate a snapshot to see revenue trends, top products, and top customers from your recent orders.</p>
                </EmptyState>
              ) : (
                <DataTable
                  columnContentTypes={['text', 'numeric', 'numeric', 'text', 'text']}
                  headings={['Period', 'Revenue', 'Orders', 'Repeat Rate', 'Actions']}
                  rows={snapshots.map((s) => [
                    <Button variant="plain" onClick={() => setActiveSnapshot(s)}>{`${s.period_start} – ${s.period_end}`}</Button>,
                    `$${s.total_revenue.toLocaleString()}`,
                    String(s.order_count),
                    impactBadge(s.repeat_customer_rate),
                    <Button variant="plain" tone="critical" size="slim" onClick={() => deleteSnapshot(s.id)}>Delete</Button>,
                  ])}
                />
              )}

              {lastPage > 1 && (
                <InlineStack align="center">
                  <Pagination
                    hasPrevious={page > 1}
                    onPrevious={() => fetchSnapshots(page - 1)}
                    hasNext={page < lastPage}
                    onNext={() => fetchSnapshots(page + 1)}
                  />
                </InlineStack>
              )}
            </BlockStack>
          </Card>
          </BlockStack>
        </Layout.Section>
      </Layout>
    </Page>
  );
}
